import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import { Suspense, useEffect } from 'react';
import ProtectedRoute from '@components/layout/ProtectedRoute';
import { ThemeProvider } from '@theme/ThemeContext';
import { AppProvider } from '@context/AppContext';
import { LoadingProvider } from '@context/LoadingContext';
import { AccountProvider } from '@context/AccountContext';
import { privateRoutes } from '@routes/privateRoutes';
import { publicRoutes } from '@routes/publicRoutes';
import { RegionalSettingsProvider } from '@context/regionalSettings';
import { GlobalLoader } from '@components/sharedComponents/GlobalLoader';
import ResponsiveToaster from '@components/sharedComponents/ResponsiveToaster';
import RoutePersistenceWrapper from '@components/layout/RoutePersistenceWrapper';
import ErrorBoundary from '@components/sharedComponents/ErrorBoundary';
import { setGlobalServiceMaintenanceHandler } from '@services/httpService';
import {
  setGlobalUnhandledRejectionHandler,
  setupGlobalErrorHandlers,
} from '@services/globalErrorService';
import { PublicRoutes } from '@enums/publicRouteEnums';
import '@fontsource/epilogue/400.css'; // Regular
import '@fontsource/epilogue/500.css'; // Medium
import '@fontsource/epilogue/600.css'; // Semi-bold
import '@fontsource/epilogue/700.css'; // Bold
import 'remixicon/fonts/remixicon.css';
import { GoogleAnalytics } from '@components/analytics/GoogleAnalytics';
import { PostHogAnalytics } from '@components/analytics/PostHogAnalytics';
import { useDocumentTitle } from '@hooks/useDocumentTitle';
import { PostHogErrorBoundary } from '@posthog/react';

const queryClient = new QueryClient();

const GlobalErrorHandler: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleServiceMaintenance = () => {
      navigate(PublicRoutes.SERVICE_MAINTENANCE, { replace: true });
    };

    setGlobalServiceMaintenanceHandler(handleServiceMaintenance);

    setGlobalUnhandledRejectionHandler(handleServiceMaintenance);

    setupGlobalErrorHandlers();

    return () => {
      setGlobalServiceMaintenanceHandler(() => {});
      setGlobalUnhandledRejectionHandler(() => {});
    };
  }, [navigate]);

  return null;
};

const DocumentTitleHandler: React.FC = () => {
  useDocumentTitle();
  return null;
};

function App() {
  return (
    <ErrorBoundary>
      <PostHogErrorBoundary>
        <RegionalSettingsProvider>
          <BrowserRouter>
            <QueryClientProvider client={queryClient}>
              <GlobalErrorHandler />
              <AppProvider>
                <RoutePersistenceWrapper>
                  <LoadingProvider>
                    <AccountProvider>
                      <ThemeProvider>
                        <DocumentTitleHandler />
                        <GoogleAnalytics />
                        <PostHogAnalytics />
                        <Suspense
                          fallback={
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100vh',
                              }}
                            >
                              <div>Loading...</div>
                            </div>
                          }
                        >
                          <GlobalLoader />
                          <ResponsiveToaster />
                          <Routes>
                            {publicRoutes.map(
                              ({ path, component: Component }) => (
                                <Route
                                  key={path}
                                  path={path}
                                  element={<Component />}
                                />
                              )
                            )}
                            <Route element={<ProtectedRoute />}>
                              {privateRoutes.map(
                                ({ path, component: Component }) => (
                                  <Route
                                    key={path}
                                    path={path}
                                    element={<Component />}
                                  />
                                )
                              )}
                              <Route
                                path="*"
                                element={<Navigate to="/" replace />}
                              />
                            </Route>
                          </Routes>
                        </Suspense>
                      </ThemeProvider>
                    </AccountProvider>
                  </LoadingProvider>
                </RoutePersistenceWrapper>
              </AppProvider>
            </QueryClientProvider>
          </BrowserRouter>
        </RegionalSettingsProvider>
      </PostHogErrorBoundary>
    </ErrorBoundary>
  );
}

export default App;
